import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useView } from '../context/ViewContext';

const ManageOrders = () => {
    const navigate = useNavigate();
    const { isOwnerView } = useView();
    const [orders, setOrders] = useState([]);
    const [entityStatus, setEntityStatus] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingEntityStatus, setLoadingEntityStatus] = useState(false);
    const [error, setError] = useState('');
    const [expandedOrderId, setExpandedOrderId] = useState(null);
    const [expandedEntities, setExpandedEntities] = useState(new Set());
    const [activeBatch, setActiveBatch] = useState(0);
    const [showDelayModal, setShowDelayModal] = useState(false);
    const [selectedOrderForDelay, setSelectedOrderForDelay] = useState(null);
    const [delayDays, setDelayDays] = useState(1);
    const [actionLoading, setActionLoading] = useState({});

    const fetchTodaysOrders = useCallback(async () => {
        try {
            setLoading(true);
            const userStr = localStorage.getItem('user');
            if (!userStr) {
                navigate('/login');
                return;
            }
            const user = JSON.parse(userStr);

            const response = await axios.get(
                `https://rotica.in/get_todays_orders.php?user_id=${user.id}&role=${isOwnerView ? 'owner' : user.role}`
            );

            if (response.data.success) {
                setOrders(response.data.orders);
            } else {
                setError(response.data.message || "Failed to fetch orders.");
            }
        } catch (err) {
            console.error(err);
            setError("Network Error: Could not fetch orders.");
        } finally {
            setLoading(false);
        }
    }, [navigate, isOwnerView]);

    const fetchEntityStatus = useCallback(async () => {
        try {
            setLoadingEntityStatus(true);
            const response = await axios.get('https://rotica.in/get_entities_prep_status.php');
            if (response.data.success) {
                setEntityStatus(response.data.entities);
            }
        } catch (err) {
            console.error('Failed to fetch entity status:', err);
        } finally {
            setLoadingEntityStatus(false);
        }
    }, []);

    useEffect(() => {
        fetchTodaysOrders();
        if (!isOwnerView) {
            fetchEntityStatus();
        }
    }, [fetchTodaysOrders, fetchEntityStatus, isOwnerView]);

    const toggleOrderExpand = (orderId) => {
        setExpandedOrderId(expandedOrderId === orderId ? null : orderId);
    };

    const toggleEntityExpand = (entityId) => {
        setExpandedEntities(prev => {
            const next = new Set(prev);
            if (next.has(entityId)) {
                next.delete(entityId);
            } else {
                next.add(entityId);
            }
            return next;
        });
    };

    const handleTogglePrepared = async (itemId, currentStatus, orderId) => {
        setActionLoading(prev => ({ ...prev, [`item_${itemId}`]: true }));

        try {
            const formData = new FormData();
            formData.append('item_id', itemId);
            formData.append('is_prepared', currentStatus ? 0 : 1);
            formData.append('app_request', 'true');

            const response = await axios.post('https://rotica.in/update_item_prepared.php', formData);

            if (response.data.success) {
                setOrders(prev => prev.map(order => {
                    if (order.id === orderId) {
                        const updatedItems = order.items.map(item =>
                            item.item_id === itemId
                                ? { ...item, is_prepared: !currentStatus }
                                : item
                        );
                        const allPrepared = updatedItems.every(item => item.is_prepared);
                        return { ...order, items: updatedItems, all_prepared: allPrepared };
                    }
                    return order;
                }));
            }
        } catch (err) {
            console.error('Failed to toggle prepared status:', err);
        } finally {
            setActionLoading(prev => ({ ...prev, [`item_${itemId}`]: false }));
        }
    };

    const handleCompleteOrder = async (orderId) => {
        setActionLoading(prev => ({ ...prev, [`complete_${orderId}`]: true }));

        try {
            const formData = new FormData();
            formData.append('order_id', orderId);

            const response = await axios.post('https://rotica.in/complete_order.php', formData);

            if (response.data.success) {
                setOrders(prev => prev.map(order =>
                    order.id === orderId
                        ? {
                            ...order,
                            is_completed: true,
                            completed_at: response.data.completed_at,
                            warning_count: response.data.warning_count,
                            alert_flag: response.data.alert_flag
                        }
                        : order
                ));

                // Show status message based on response
                if (response.data.status === 'alert') {
                    alert(response.data.message);
                } else if (response.data.status === 'warning') {
                    alert(response.data.message);
                }
            } else {
                alert(response.data.message);
            }
        } catch (err) {
            console.error('Failed to complete order:', err);
            alert('Failed to complete order. Please try again.');
        } finally {
            setActionLoading(prev => ({ ...prev, [`complete_${orderId}`]: false }));
        }
    };

    const openDelayModal = (order) => {
        setSelectedOrderForDelay(order);
        setDelayDays(1);
        setShowDelayModal(true);
    };

    const handleDelayOrder = async () => {
        if (!selectedOrderForDelay) return;

        setActionLoading(prev => ({ ...prev, [`delay_${selectedOrderForDelay.id}`]: true }));

        const today = new Date();
        const resumptionDate = new Date(today);
        resumptionDate.setDate(resumptionDate.getDate() + delayDays);

        try {
            const formData = new FormData();
            formData.append('order_id', selectedOrderForDelay.id);
            formData.append('delay_days', delayDays);
            formData.append('resumption_date', resumptionDate.toISOString().split('T')[0]);

            const response = await axios.post('https://rotica.in/delay_order.php', formData);

            if (response.data.success) {
                alert(response.data.message);
                setShowDelayModal(false);
                setSelectedOrderForDelay(null);
                fetchTodaysOrders(); // Refresh the list
            } else {
                alert(response.data.message);
            }
        } catch (err) {
            console.error('Failed to delay order:', err);
            alert('Failed to delay order. Please try again.');
        } finally {
            setActionLoading(prev => ({ ...prev, [`delay_${selectedOrderForDelay?.id}`]: false }));
        }
    };

    const getFilteredOrders = () => {
        if (activeBatch === 0) return orders;

        return orders.filter(order => {
            if (!order.delivery_time) return false;
            // Handle both XX:XX:XX and X:XX formats
            const hour = parseInt(order.delivery_time.split(':')[0]);

            if (activeBatch === 1) return hour >= 6 && hour <= 10;
            if (activeBatch === 2) return hour >= 12 && hour <= 15;
            if (activeBatch === 3) return hour >= 18 && hour <= 21;
            return true;
        });
    };

    const filteredOrders = getFilteredOrders();

    return (
        <div className="manage-orders-page">
            <header className="production-header">
                <h1 className="page-title">Manage Orders</h1>
                <p className="page-subtitle">Today's orders and preparation status</p>
            </header>

            {/* Batch Selector */}
            <div className="batch-control-bar">
                <div className="segmented-control">
                    {[
                        { id: 0, label: 'All Orders', time: '' },
                        { id: 1, label: 'Batch 1', time: '7AM - 10AM' },
                        { id: 2, label: 'Batch 2', time: '12PM - 3PM' },
                        { id: 3, label: 'Batch 3', time: '6PM - 9PM' }
                    ].map(batch => (
                        <button
                            key={batch.id}
                            className={`segment-btn ${activeBatch === batch.id ? 'active' : ''}`}
                            onClick={() => setActiveBatch(batch.id)}
                        >
                            <span className="segment-label">{batch.label}</span>
                            {batch.time && <span className="segment-time">{batch.time}</span>}
                        </button>
                    ))}
                </div>
            </div>

            {/* Entity Preparation Status (Owner only) */}
            {isOwnerView && (
                <section className="entity-status-section">
                    <div className="section-header">
                        <h2>Entity Preparation Status</h2>
                        <button className="refresh-btn" onClick={fetchEntityStatus} disabled={loadingEntityStatus}>
                            {loadingEntityStatus ? '...' : 'Refresh'}
                        </button>
                    </div>

                    <div className="table-card">
                        <table className="orders-manage-table entity-status-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '40px' }}></th>
                                    <th>Entity Name</th>
                                    <th>Type</th>
                                    <th>Progress</th>
                                    <th>Status</th>
                                    <th>Items</th>
                                </tr>
                            </thead>
                            <tbody>
                                {entityStatus.length > 0 ? (
                                    entityStatus.map(entity => {
                                        const entityIdKey = `${entity.type}-${entity.id}`;
                                        const isExpanded = expandedEntities.has(entityIdKey);

                                        // Filter orders within each entity based on batch
                                        const allEntityOrders = entity.orders || [];
                                        const filteredEntityOrders = activeBatch === 0
                                            ? allEntityOrders
                                            : allEntityOrders.filter(o => {
                                                const hour = parseInt(o.delivery_time?.split(':')[0] || '0');
                                                if (activeBatch === 1) return hour >= 6 && hour <= 10;
                                                if (activeBatch === 2) return hour >= 12 && hour <= 15;
                                                if (activeBatch === 3) return hour >= 18 && hour <= 21;
                                                return true;
                                            });

                                        // Recalculate progress for the batch
                                        const batchTotal = filteredEntityOrders.reduce((acc, o) => acc + (parseInt(o.total_items) || 0), 0);
                                        const batchPrepared = filteredEntityOrders.reduce((acc, o) => acc + (parseInt(o.prepared_items) || 0), 0);
                                        const batchPercentage = batchTotal > 0 ? Math.round((batchPrepared / batchTotal) * 100) : 0;
                                        const batchStatus = batchTotal === 0 ? 'Pending' : (batchPercentage === 100 ? 'Ready' : (batchPercentage > 0 ? 'Preparing' : 'Pending'));

                                        return (
                                            <React.Fragment key={entityIdKey}>
                                                <tr
                                                    className={`entity-row ${isExpanded ? 'expanded' : ''}`}
                                                    onClick={() => toggleEntityExpand(entityIdKey)}
                                                >
                                                    <td className="expand-cell">
                                                        <span className={`expand-icon ${isExpanded ? 'rotated' : ''}`}>
                                                            ▶
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className="entity-info">
                                                            <span className="entity-name">{entity.name}</span>
                                                            <span className="entity-id">ID: #{entity.id}</span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className={`type-badge ${entity.type}`}>
                                                            {entity.type.charAt(0).toUpperCase() + entity.type.slice(1)}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className="progress-container">
                                                            <div className="progress-bar-bg">
                                                                <div
                                                                    className="progress-bar-fill"
                                                                    style={{ width: `${batchPercentage}%` }}
                                                                ></div>
                                                            </div>
                                                            <span className="progress-text">{batchPercentage}%</span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className={`status-badge ${batchStatus.toLowerCase()}`}>
                                                            {batchStatus}
                                                        </span>
                                                    </td>
                                                    <td>{batchPrepared} / {batchTotal}</td>
                                                </tr>
                                                {isExpanded && (
                                                    <tr className="expanded-row">
                                                        <td colSpan="6">
                                                            <div className="expanded-content">
                                                                <h4 className="sub-table-title">Scheduled Assignments ({activeBatch === 0 ? 'All Day' : `Batch ${activeBatch}`})</h4>
                                                                {filteredEntityOrders.length > 0 ? (
                                                                    <table className="mini-orders-table">
                                                                        <thead>
                                                                            <tr>
                                                                                <th>Order ID</th>
                                                                                <th>Customer</th>
                                                                                <th>Time</th>
                                                                                <th>Progress</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {filteredEntityOrders.map(order => (
                                                                                <tr key={order.id}>
                                                                                    <td>#{order.id}</td>
                                                                                    <td>{order.customer_name}</td>
                                                                                    <td>{order.delivery_time}</td>
                                                                                    <td>
                                                                                        {order.prepared_items}/{order.total_items} items
                                                                                    </td>
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </table>
                                                                ) : (
                                                                    <p className="no-data">No orders assigned for this batch.</p>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })
                                ) : (
                                    <tr><td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>No entity data available.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}

            {/* Orders Table */}
            <div className="production-data-container">
                {loading && <div className="loading-container">Loading today's orders...</div>}

                {error && (
                    <div className="error-container">
                        <p>{error}</p>
                        <button onClick={fetchTodaysOrders}>Retry</button>
                    </div>
                )}

                {!loading && !error && (
                    <div className="table-card">
                        {filteredOrders.length > 0 ? (
                            <table className="orders-manage-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: '40px' }}></th>
                                        <th>Order ID</th>
                                        <th>Customer</th>
                                        {isOwnerView && <th>Assigned To</th>}
                                        <th>Delivery Time</th>
                                        <th>Items</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredOrders.map(order => (
                                        <React.Fragment key={order.id}>
                                            <tr
                                                className={`order-row ${expandedOrderId === order.id ? 'expanded' : ''} ${order.alert_flag ? 'alert-row' : ''}`}
                                                onClick={() => toggleOrderExpand(order.id)}
                                            >
                                                <td className="expand-cell">
                                                    <span className={`expand-icon ${expandedOrderId === order.id ? 'rotated' : ''}`}>
                                                        ▶
                                                    </span>
                                                </td>
                                                <td>#{order.id}</td>
                                                <td>
                                                    <div className="customer-info">
                                                        <span className="customer-name">{order.customer_name}</span>
                                                        <span className="customer-phone">{order.contact_no}</span>
                                                    </div>
                                                </td>
                                                {isOwnerView && (
                                                    <td>
                                                        <div className="entity-cell">
                                                            <span className="entity-name">{order.assigned_name || 'Unassigned'}</span>
                                                            <span className="entity-id" style={{ fontSize: '11px' }}>
                                                                {order.assigned_type ? `ID: #${order.assigned_id}` : ''}
                                                            </span>
                                                            <div style={{ marginTop: 4 }}>
                                                                <span
                                                                    className={`assigned-badge ${order.assigned_type || 'unassigned'}`}
                                                                >
                                                                    {order.assigned_type
                                                                        ? order.assigned_type.charAt(0).toUpperCase() + order.assigned_type.slice(1)
                                                                        : 'Unassigned'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                )}
                                                <td>
                                                    <span className="time-badge">{order.delivery_time_display}</span>
                                                </td>
                                                <td>
                                                    <span className="item-count-badge">
                                                        {order.items?.length || 0} items
                                                    </span>
                                                </td>
                                                <td>
                                                    {order.is_completed ? (
                                                        <span className="status-badge completed">Completed</span>
                                                    ) : order.all_prepared ? (
                                                        <span className="status-badge ready">Ready</span>
                                                    ) : (
                                                        <span className="status-badge preparing">Preparing</span>
                                                    )}
                                                    {order.warning_count > 0 && (
                                                        <span className="warning-badge" title={`${order.warning_count} warning(s)`}>
                                                            ⚠️ {order.warning_count}
                                                        </span>
                                                    )}
                                                    {order.alert_flag && (
                                                        <span className="alert-badge" title="Alert flag set">
                                                            🚨
                                                        </span>
                                                    )}
                                                </td>
                                                <td onClick={(e) => e.stopPropagation()}>
                                                    {!isOwnerView && !order.is_completed && (
                                                        <div className="action-buttons">
                                                            <button
                                                                className={`complete-btn ${!order.all_prepared ? 'disabled' : ''}`}
                                                                disabled={!order.all_prepared || actionLoading[`complete_${order.id}`]}
                                                                onClick={() => handleCompleteOrder(order.id)}
                                                            >
                                                                {actionLoading[`complete_${order.id}`] ? '...' : 'Complete'}
                                                            </button>
                                                            <button
                                                                className="delay-btn"
                                                                onClick={() => openDelayModal(order)}
                                                            >
                                                                Delay
                                                            </button>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>

                                            {/* Expanded Items Row */}
                                            {expandedOrderId === order.id && (
                                                <tr className="expanded-row">
                                                    <td colSpan={isOwnerView ? "8" : "7"}>
                                                        <div className="expanded-content">
                                                            <div className="order-address">
                                                                <strong>📍 Address:</strong> {order.address_text}
                                                            </div>
                                                            {order.remarks && (
                                                                <div className="order-remarks">
                                                                    <strong>📝 Remarks:</strong> {order.remarks}
                                                                </div>
                                                            )}
                                                            <div className="items-list">
                                                                <h4>Order Items</h4>
                                                                <table className="items-table">
                                                                    <thead>
                                                                        <tr>
                                                                            <th>Product</th>
                                                                            <th>Quantity</th>
                                                                            <th>Pack Of</th>
                                                                            <th>Prepared</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {order.items?.map(item => (
                                                                            <tr key={item.item_id}>
                                                                                <td>{item.name}</td>
                                                                                <td>{item.quantity}</td>
                                                                                <td>{item.pack_size || 1}</td>
                                                                                <td>
                                                                                    <label className="status-toggle">
                                                                                        <input
                                                                                            type="checkbox"
                                                                                            checked={item.is_prepared}
                                                                                            disabled={isOwnerView || order.is_completed || actionLoading[`item_${item.item_id}`]}
                                                                                            onChange={() => handleTogglePrepared(item.item_id, item.is_prepared, order.id)}
                                                                                        />
                                                                                        <span className="slider"></span>
                                                                                    </label>
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            !isOwnerView && (
                                <div className="empty-table-state">
                                    <p>No orders scheduled for today.</p>
                                </div>
                            )
                        )}
                    </div>
                )}
            </div>

            {/* Delay Modal */}
            {showDelayModal && (
                <div className="modal-overlay" onClick={() => setShowDelayModal(false)}>
                    <div className="delay-modal" onClick={(e) => e.stopPropagation()}>
                        <h3>Delay Order #{selectedOrderForDelay?.id}</h3>
                        <p>How many days do you want to delay this order?</p>
                        <div className="delay-input-group">
                            <button
                                className="delay-adjust-btn"
                                onClick={() => setDelayDays(Math.max(1, delayDays - 1))}
                            >
                                -
                            </button>
                            <span className="delay-days-display">{delayDays} day{delayDays > 1 ? 's' : ''}</span>
                            <button
                                className="delay-adjust-btn"
                                onClick={() => setDelayDays(delayDays + 1)}
                            >
                                +
                            </button>
                        </div>
                        <p className="delay-note">
                            The order will be temporarily reassigned to another available distributor/manufacturer during this period.
                        </p>
                        <div className="modal-actions">
                            <button className="modal-cancel-btn" onClick={() => setShowDelayModal(false)}>
                                Cancel
                            </button>
                            <button
                                className="modal-confirm-btn"
                                onClick={handleDelayOrder}
                                disabled={actionLoading[`delay_${selectedOrderForDelay?.id}`]}
                            >
                                {actionLoading[`delay_${selectedOrderForDelay?.id}`] ? 'Processing...' : 'Confirm Delay'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManageOrders;
